import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiClock, FiSend, FiAlertTriangle } from 'react-icons/fi';
import api from '../../utils/api';
import * as S from '../../styles/inlineStyles';

const ExamPage = () => {
    const { examId } = useParams();
    const navigate = useNavigate();
    const [exam, setExam] = useState(null);
    const [paper, setPaper] = useState(null);
    const [answers, setAnswers] = useState(() => {
        try {
            const saved = localStorage.getItem(`exam_answers_${examId}`);
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [examStatus, setExamStatus] = useState('loading');
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const timerRef = useRef(null);
    const submittedRef = useRef(false);

    // Proctoring states
    const [warnings, setWarnings] = useState(0);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const tabSwitchCountRef = useRef(0);
    const lastViolatedAt = useRef(0);
    const statusRef = useRef(examStatus);
    const mountTimeRef = useRef(Date.now());
    useEffect(() => { statusRef.current = examStatus; }, [examStatus]);

    const answersRef = useRef(answers);
    useEffect(() => {
        answersRef.current = answers;
        if (answers && answers.length > 0) {
            localStorage.setItem(`exam_answers_${examId}`, JSON.stringify(answers));
        }
    }, [answers, examId]);

    const handleAutoSubmit = useCallback(async () => {
        if (submittedRef.current) return;
        
        // Anti-loop guard: Don't allow submission within 10s of page load/restart
        if (Date.now() - mountTimeRef.current < 10000) {
            console.log("[ANTI-LOOP] Ignoring auto-submit too soon after load.");
            return;
        }

        submittedRef.current = true;

        if (timerRef.current) clearInterval(timerRef.current);

        const proctorStats = {
            tabSwitchCount: tabSwitchCountRef.current,
        };

        try {
            await api.post(`/api/student/exams/${examId}/submit`, { answers: answersRef.current, isAutoSubmitted: true, proctorStats });
            window.scrollTo(0, 0);
            setExamStatus('submitted');
        } catch (err) {
            console.error('Auto-submit failed', err);
            setExamStatus('submitted');
        }
    }, [examId]);

    const handleViolation = useCallback((message, type = 'other') => {
        if (submittedRef.current) return;
        const now = Date.now();
        if (now - lastViolatedAt.current < 4000) return; 
        lastViolatedAt.current = now;

        if (type === 'tab') {
            tabSwitchCountRef.current += 1;
        }

        // Push live stats to DB (Heartbeat)
        api.post(`/api/student/exams/${examId}/heartbeat`, {
            tabSwitchCount: tabSwitchCountRef.current,
            answers: answersRef.current
        }).catch(() => { });

        setWarnings(prev => {
            const next = prev + 1;
            setWarningMessage(`${message}. Warning ${next} of 50!`);
            setShowWarningModal(true);
            setTimeout(() => setShowWarningModal(false), 3000);

            if (next >= 50) {
                alert("Maximum warnings reached (50). Your exam has been automatically submitted due to rules violations.");
                handleAutoSubmit();
            }
            return next;
        });
    }, [examId, handleAutoSubmit]);

    const setFileForQuestion = (qi, url) => {
        setAnswers(prev => { const u = [...prev]; u[qi] = { ...u[qi], uploadedFileUrl: url, uploading: false }; return u; });
    };

    const handleFileUpload = async (qi, file) => {
        if (!file) return;
        setAnswers(prev => { const u = [...prev]; u[qi] = { ...u[qi], uploading: true }; return u; });
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await api.post('/api/student/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setFileForQuestion(qi, res.data.fileUrl);
        } catch (err) {
            console.error(err);
            alert('Failed to upload file. Please try again.');
            setAnswers(prev => { const u = [...prev]; u[qi] = { ...u[qi], uploading: false }; return u; });
        }
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const getTimerClass = () => {
        if (timeLeft <= 60) return 'danger';
        if (timeLeft <= 300) return 'warning';
        return 'safe';
    };

    const selectOption = (qi, oi) => {
        setAnswers(prev => { const u = [...prev]; u[qi] = { ...u[qi], selectedOption: oi }; return u; });
    };

    const setDescriptiveAnswer = (qi, text) => {
        setAnswers(prev => { const u = [...prev]; u[qi] = { ...u[qi], answerText: text }; return u; });
    };

    const fetchExamData = useCallback(async () => {
        try {
            const res = await api.get(`/api/student/exams/${examId}/paper`);
            const data = res.data;
            setExam(data.exam);

            // ── RESTART DETECTION ──
            if (statusRef.current === 'submitted' && !data.alreadySubmitted) {
                console.log("[RESTART] Detected submission reset. Re-enabling exam access.");
                submittedRef.current = false;
                // FORCE RELOAD is the safest way to clear all background intervals and stale state
                window.location.reload();
                return;
            }

            if (data.alreadySubmitted) { 
                submittedRef.current = true;
                setExamStatus('submitted'); 
                return; 
            }
            if (data.exam.state === 'ENDED') { setExamStatus('ended'); return; }
            if (data.paper && data.exam.state === 'LIVE') {
                setPaper(data.paper);
                setAnswers(prev => {
                    if (prev && prev.length > 0) return prev;
                    if (data.existingAnswers && Array.isArray(data.existingAnswers) && data.existingAnswers.length > 0) {
                        return data.existingAnswers;
                    }
                    return new Array(data.paper.questions?.length || 0).fill(null).map(() => ({
                        selectedOption: null, answerText: '', uploadedFileUrl: null, uploading: false
                    }));
                });

                const now = new Date();
                const startTime = new Date(data.exam.startTime);
                let baseTime = (now > startTime) && startTime.getTime() ? startTime : now;
                let endTime = new Date(baseTime.getTime() + data.exam.duration * 60 * 1000);

                const saneDuration = (data.exam.duration && data.exam.duration > 0) ? data.exam.duration : 180;
                if (now >= endTime) {
                    baseTime = now;
                    endTime = new Date(baseTime.getTime() + saneDuration * 60 * 1000);
                } else if (endTime.getTime() - now.getTime() < 60000) {
                    endTime = new Date(now.getTime() + saneDuration * 60 * 1000);
                }
                setTimeLeft(Math.max(60, Math.floor((endTime - now) / 1000)));
                setExamStatus('live');

            } else {
                if (data.exam.state === 'SCHEDULED') setExamStatus('waiting');
                else if (data.exam.state === 'REVEAL') setExamStatus('reveal');
                else setExamStatus('waiting');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load exam');
            setExamStatus('error');
        }
    }, [examId]); // Removed examStatus from dependencies to avoid infinite loops

    useEffect(() => { fetchExamData(); }, [fetchExamData]);

    useEffect(() => {
        if (examStatus !== 'live' || timeLeft === null) return;
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev !== null && prev > 0 && prev <= 1) { 
                    clearInterval(timerRef.current); 
                    if (!submittedRef.current) handleAutoSubmit(); 
                    return 0; 
                }
                if (prev <= 0) return 0;
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [examStatus, timeLeft === null, handleAutoSubmit, examId]);

    useEffect(() => {
        if (examStatus === 'submitted' || examStatus === 'error' || examStatus === 'ended') return;
        
        // Fast polling (15s) for waiting/reveal to start exam quickly
        // Slow polling (60s) for live to detect if invigilator ended exam
        const intervalTime = (examStatus === 'waiting' || examStatus === 'reveal') ? 15000 : 60000;
        
        const interval = setInterval(fetchExamData, intervalTime);
        return () => clearInterval(interval);
    }, [examStatus, fetchExamData]);


    // Tab & Window tracking
    useEffect(() => {
        if (examStatus !== 'live' || submittedRef.current) return;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                handleViolation("You left the exam tab", 'tab');
            }
        };
        const handleBlur = () => {
            handleViolation("You switched away from the exam window", 'tab');
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("blur", handleBlur);
        
        // CRITICAL FIX: Always initialize exam in DB immediately when going live
        api.post(`/api/student/exams/${examId}/initialize`).catch((e) => {
            console.warn('Init failed (non-critical):', e.message);
        });

        // Start periodic heartbeat to DB (Sync answers every 30s as backup)
        const dbHeartbeatInterval = setInterval(() => {
            if (submittedRef.current) {
                clearInterval(dbHeartbeatInterval);
                return;
            }
            const currentAnswers = answersRef.current;
            if (currentAnswers && currentAnswers.length > 0) {
                api.post(`/api/student/exams/${examId}/heartbeat`, {
                    tabSwitchCount: tabSwitchCountRef.current,
                    answers: currentAnswers
                }).catch(() => { });
            }
        }, 20000);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("blur", handleBlur);
            clearInterval(dbHeartbeatInterval);
        };
    }, [examStatus, handleViolation, examId]);

    const handleSubmit = async () => {
        if (submittedRef.current) return;

        submittedRef.current = true;
        setSubmitting(true);
        setShowConfirm(false);

        if (timerRef.current) clearInterval(timerRef.current);

        const proctorStats = {
            tabSwitchCount: tabSwitchCountRef.current,
        };

        // CRITICAL FIX: Always use the most recent answers. Prefer ref, fall back to localStorage.
        let finalAnswers = answersRef.current;
        if (!finalAnswers || finalAnswers.length === 0) {
            try {
                const saved = localStorage.getItem(`exam_answers_${examId}`);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (parsed && parsed.length > 0) {
                        finalAnswers = parsed;
                    }
                }
            } catch (e) { }
        }

        try {
            await api.post(`/api/student/exams/${examId}/submit`, {
                answers: finalAnswers || [],
                isAutoSubmitted: false,
                proctorStats
            });
            setExamStatus('submitted');
            localStorage.removeItem(`exam_answers_${examId}`);
        } catch (err) {
            const msg = err.response?.data?.message || '';
            if (err.response?.status === 400 && msg.toLowerCase().includes('already')) {
                setExamStatus('submitted');
                localStorage.removeItem(`exam_answers_${examId}`);
                return;
            }
            submittedRef.current = false;
            setSubmitting(false);
            alert(`Submission failed: ${msg || 'Check your internet connection and try again.'}`);
        }
    };

    const answeredCount = answers.filter(a => a.selectedOption !== null || a.answerText.trim() !== '' || a.uploadedFileUrl).length;

    // === Countdown/Status Page ===
    const centerPage = {
        minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 32,
    };

    if (examStatus === 'loading') {
        return <div style={S.loadingContainer}><div style={S.spinner} /><p style={S.loadingText}>Loading exam...</p></div>;
    }

    if (examStatus === 'error') {
        return (
            <div style={centerPage}>
                <span style={{ fontSize: 56, marginBottom: 16 }}>⚠️</span>
                <h2 style={{ ...S.pageTitle, marginBottom: 8 }}>Unable to Load Exam</h2>
                <p style={{ color: S.colors.textSecondary, marginBottom: 24, maxWidth: 400 }}>{error}</p>
                <button className="btn-hover" style={S.btnPrimary} onClick={() => navigate('/student/exams')}>Back to Exams</button>
            </div>
        );
    }

    if (examStatus === 'submitted') {
        return (
            <div style={centerPage}>
                <span style={{ fontSize: 56, marginBottom: 16 }}>✅</span>
                <h2 style={{ ...S.pageTitle, marginBottom: 8 }}>Exam Submitted!</h2>
                <p style={{ color: S.colors.textSecondary, maxWidth: 400, marginBottom: 24 }}>Your answers have been submitted successfully. Results will be available after evaluation.</p>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button className="btn-hover" style={S.btnPrimary} onClick={() => navigate('/student/dashboard')}>Back to Dashboard</button>
                    <button className="btn-hover" style={{ ...S.btnOutline, background: '#f3f4f6' }} onClick={() => { setExamStatus('loading'); fetchExamData(); }}>Re-check Status</button>
                </div>
            </div>
        );
    }

    if (examStatus === 'waiting') {
        return (
            <div style={centerPage}>
                <span style={{ fontSize: 56, marginBottom: 16 }}>⏳</span>
                <h2 style={{ ...S.pageTitle, marginBottom: 8 }}>Waiting for Invigilator</h2>
                <p style={{ color: S.colors.textSecondary, marginBottom: 16 }}>The invigilator has not started the exam session yet. Please wait for the "Start Hall" action.</p>
                {exam && (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: 14, color: S.colors.textSecondary }}>Exam scheduled for: <strong>{new Date(exam.startTime).toLocaleString()}</strong></p>
                        <p style={{ fontSize: 13, color: S.colors.textLight, marginTop: 8, fontWeight: 700 }}>This page will automatically refresh once the exam begins.</p>
                    </div>
                )}
            </div>
        );
    }

    if (examStatus === 'ended') {
        return (
            <div style={centerPage}>
                <span style={{ fontSize: 56, marginBottom: 16 }}>🏁</span>
                <h2 style={{ ...S.pageTitle, marginBottom: 8 }}>Exam Has Ended</h2>
                <p style={{ color: S.colors.textSecondary, marginBottom: 24 }}>This exam has already ended.</p>
                <button className="btn-hover" style={S.btnPrimary} onClick={() => navigate('/student/dashboard')}>Back to Dashboard</button>
            </div>
        );
    }

    if (examStatus === 'reveal' && paper) {
        return (
            <div style={centerPage}>
                <span style={{ fontSize: 56, marginBottom: 16 }}>📋</span>
                <h2 style={{ ...S.pageTitle, marginBottom: 8 }}>Ready to Begin</h2>
                <p style={{ color: S.colors.textSecondary, maxWidth: 450, marginBottom: 16 }}>Your paper is ready! Please wait for the invigilator to click <strong>"Start Hall"</strong> on their dashboard to begin the writing time.</p>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 14, color: S.colors.textSecondary }}>Official Start: <strong>{new Date(exam.startTime).toLocaleString()}</strong></p>
                    <p style={{ fontSize: 13, color: S.colors.textLight, marginTop: 8, fontWeight: 700 }}>Do not refresh. This page will unlock automatically.</p>
                </div>
            </div>
        );
    }

    if (!paper || !paper.questions) {
        if (examStatus === 'error') {
            return (
                <div style={centerPage}>
                    <FiAlertTriangle size={56} color="#ef4444" style={{ marginBottom: 16 }} />
                    <h2 style={{ ...S.pageTitle, color: '#ef4444' }}>Error Loading Exam</h2>
                    <p style={{ color: S.colors.textSecondary }}>{error || 'Could not resolve question paper for your batch.'}</p>
                    <button className="btn-hover" style={{ ...S.btnPrimary, marginTop: 16 }} onClick={() => navigate('/student/dashboard')}>Back to Dashboard</button>
                </div>
            );
        }
        return <div style={S.loadingContainer}><div style={S.spinner} /><p style={S.loadingText}>Loading questions...</p></div>;
    }

    const question = paper.questions[currentQuestion];

    return (
        <div>
            {/* Exam Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: S.colors.white, padding: '16px 20px', borderRadius: 16,
                border: `1px solid ${S.colors.border}`, marginBottom: 16, flexWrap: 'wrap', gap: 12,
            }}>
                <div>
                    <h2 style={{ fontFamily: S.font, fontSize: 18, fontWeight: 800, margin: 0, color: S.colors.textPrimary }}>{exam?.subject_name || 'Exam'} ({exam?.subject_code})</h2>
                    <span style={{ fontSize: 13, color: S.colors.textSecondary }}>{paper.title} • Question {currentQuestion + 1} of {paper.questions.length}</span>
                </div>
                <div style={S.examTimerStyle(getTimerClass())}>
                    <FiClock /> {formatTime(timeLeft)}
                </div>
            </div>


            {/* Warning Overlay Modal */}
            {showWarningModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(220,38,38,0.2)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ background: '#fff', padding: '30px 40px', borderRadius: 24, border: '4px solid #ef4444', textAlign: 'center', boxShadow: '0 20px 50px rgba(239,68,68,0.4)', animation: 'shake 0.4s ease-in-out' }}>
                        <FiAlertTriangle size={64} color="#ef4444" style={{ marginBottom: 16 }} />
                        <h2 style={{ fontSize: 24, fontWeight: 900, color: '#991b1b', margin: '0 0 10px 0', textTransform: 'uppercase' }}>Violation Detected!</h2>
                        <p style={{ fontSize: 16, fontWeight: 700, color: '#b91c1c', margin: 0 }}>{warningMessage}</p>
                    </div>
                    <style>
                        {`
                        @keyframes shake {
                          0% { transform: translateX(0); }
                          25% { transform: translateX(-10px); }
                          50% { transform: translateX(10px); }
                          75% { transform: translateX(-10px); }
                          100% { transform: translateX(0); }
                        }
                        `}
                    </style>
                </div>
            )}

            {/* Question Card */}
            <div style={S.questionCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                            width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #059669, #10b981)',
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14
                        }}>{currentQuestion + 1}</span>
                        <span style={{ fontSize: 14, color: S.colors.textSecondary }}>{question.questionText?.substring(0, 50)}...</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <span style={S.badge(question.type === 'mcq' ? 'LOCKED' : 'EVALUATION')}>{question.type}</span>
                        <span style={S.badge('RESULT')}>{question.marks} marks</span>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 20 }}>
                    <div style={{ fontSize: 16, fontWeight: 500, color: S.colors.textPrimary, lineHeight: 1.6 }}>
                        {question.questionText}
                    </div>
                    {question.imageUrl && (
                        <div style={{ marginTop: 12 }}>
                            <img src={api.defaults.baseURL + question.imageUrl} alt="Question Graphic" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 12, border: '1px solid #e2e8f0' }} />
                        </div>
                    )}
                </div>

                {question.type === 'mcq' ? (
                    <div>
                        {question.options?.map((opt, oIndex) => (
                            <div key={oIndex} className="option-hover" style={S.mcqOption(answers[currentQuestion]?.selectedOption === oIndex)}
                                onClick={() => selectOption(currentQuestion, oIndex)}>
                                <div style={S.optionRadio(answers[currentQuestion]?.selectedOption === oIndex)} />
                                <span style={{ fontWeight: 600, color: S.colors.textSecondary, marginRight: 4 }}>{String.fromCharCode(65 + oIndex)}.</span>
                                <span>{opt.text}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <textarea
                        className="input-focus"
                        placeholder="Type your answer here..."
                        style={S.textarea}
                        value={answers[currentQuestion]?.answerText || ''}
                        onChange={(e) => setDescriptiveAnswer(currentQuestion, e.target.value)}
                    />
                )}

                {question.requiresUpload && (
                    <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
                            📎 {question.upload_title || 'Upload File'}
                        </div>
                        {answers[currentQuestion]?.uploadedFileUrl ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 13, color: '#059669', fontWeight: 800 }}>✓ File Uploaded</span>
                                    <a href={api.defaults.baseURL + answers[currentQuestion].uploadedFileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>View File</a>
                                </div>
                                <button onClick={() => setFileForQuestion(currentQuestion, null)} style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Remove</button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={(e) => handleFileUpload(currentQuestion, e.target.files[0])}
                                    style={{ fontSize: 13 }}
                                    disabled={answers[currentQuestion]?.uploading}
                                />
                                {answers[currentQuestion]?.uploading && <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>Uploading...</span>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Question Navigator */}
            <div style={{
                background: S.colors.white, padding: 16, borderRadius: 16, border: `1px solid ${S.colors.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
            }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {paper.questions.map((_, i) => (
                        <button key={i} className="dot-hover" style={S.navDot(i === currentQuestion, answers[i]?.selectedOption !== null || answers[i]?.answerText?.trim() || answers[i]?.uploadedFileUrl)}
                            onClick={() => setCurrentQuestion(i)}>
                            {i + 1}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: S.colors.textSecondary }}>{answeredCount}/{paper.questions.length} answered</span>
                    <button className="btn-hover" style={S.btnPrimary} onClick={() => setShowConfirm(true)}>
                        <FiSend style={{ marginRight: 4 }} /> Submit Exam
                    </button>
                </div>
            </div>

            {/* Confirm Modal */}
            {showConfirm && (
                <div style={S.confirmOverlay} className="modal-animate">
                    <div style={S.confirmModal} className="modal-content-animate">
                        <FiAlertTriangle style={{ fontSize: 40, color: '#f59e0b', marginBottom: 16 }} />
                        <h3 style={{ ...S.pageTitle, fontSize: 20, marginBottom: 8 }}>Submit Exam?</h3>
                        <p style={{ color: S.colors.textSecondary, marginBottom: 20 }}>
                            You have answered <strong>{answeredCount}</strong> of <strong>{paper.questions.length}</strong> questions.
                            {answeredCount < paper.questions.length && (
                                <span style={{ color: S.colors.danger }}> Some questions are unanswered!</span>
                            )}
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <button className="btn-outline-hover" style={S.btnOutline} onClick={() => setShowConfirm(false)}>Continue Exam</button>
                            <button className="btn-hover" style={S.btnPrimary} onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Submitting...' : 'Yes, Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamPage;
